import { I18n } from "@assets/strings";
import React, { Fragment, useEffect } from "react";
import { Link } from "react-router";
import { Redirect } from "@core/routing/redirect";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile, useIsPerson } from "@domain/currentProfile/hooks";
import { useDetailedArticle } from "@domain/lms/article/hooks";
import { useOneCallTimer } from "@utils/hooks";
import { AppAuthIncentive } from "@ui/components/appAuthIncentive";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { EditIcon } from "@ui/components/icons";
import { stack } from "@ui/components/layout";
import { FullPage } from "@ui/components/page";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { ArticlePresentation } from "./articlePresentation";

type ArticlePageProps =
	| (React.HTMLAttributes<HTMLDivElement> & {
			articleId: string;
			articleSlug?: undefined;
			action?: "NEW_INSIGHT_EMAIL" | "NEW_INSIGHT_INSTRUCTIVE_EMAIL" | "FEEDBACK_INSIGHTS_EMAIL";
	  })
	| {
			articleSlug: string;
			articleId?: undefined;
			action?: "NEW_INSIGHT_EMAIL" | "NEW_INSIGHT_INSTRUCTIVE_EMAIL" | "FEEDBACK_INSIGHTS_EMAIL";
	  };
const DO_COUNT_VIEW_TIME = 10000;
export const ArticlePage: React.FC<ArticlePageProps> = ({ articleId, articleSlug, action, ...htmlProps }) => {
	if (!articleId && !articleSlug) {
		return <Redirect to={Path.Root} />;
	}
	const currentProfile = useCurrentProfile();
	const { articleService } = useServices();
	//const { loading, result: article, error } = useArticle(articleId || "", articleSlug);
	const { loading, result: article, error } = useDetailedArticle(articleId || "", articleSlug);

	const isAuthor = useIsPerson(article?.author ?? {});
	const { trackingService } = useServices();
	useEffect(() => {
		if (action === "NEW_INSIGHT_EMAIL") {
			trackingService.track("trackEmail - NEW_INSIGHT_EMAIL");
		}
		if (action === "NEW_INSIGHT_INSTRUCTIVE_EMAIL") {
			trackingService.track("trackEmail - NEW_INSIGHT_INSTRUCTIVE_EMAIL");
		}
		if (action === "FEEDBACK_INSIGHTS_EMAIL") {
			trackingService.track("trackEmail - FEEDBACK_INSIGHTS_EMAIL");
		}
	}, []);

	useOneCallTimer(DO_COUNT_VIEW_TIME, article && !isAuthor ? () => articleService.incrementView(article) : undefined, [
		article,
	]);
	if ((!articleId && !articleSlug) || (error?.code === 404 && !article && !loading)) {
		return <NotFoundPage message={I18n.t("404.article")} />;
	}

	return !article ? (
		loading ? (
			<ClipLoader />
		) : null
	) : (
		<Container
			{...htmlProps}
			header={
				article ? (
					isAuthor ? (
						<Link to={Path.ArticleEditor(article.id)}>
							<HeaderAction>
								{I18n.t("edit")}
								<EditIcon />
							</HeaderAction>
						</Link>
					) : (
						<Fragment>{}</Fragment>
						// <HeaderAction onClick={() => articleService.toggleFavorite(article)}>
						// 	<FavoriteOffIcon toggled={article.isFavorite} />
						// </HeaderAction>
					)
				) : (
					<></>
				)
			}
		>
			<SubContainer>
				{/* <HelmetInfos
					title={article.title}
					description={[
						article.author && fullName(article.author),
						I18n.t(`category.${article.category}`),
						article.description.getPlainText(),
					]
						.filter(Boolean)
						.join(" - ")}
					canonicalLink={article.reference ?? `${Config.APP_BASE_URL}${location.pathname}`}
				/> */}
				{/* <Seo
					type="website"
					title={article.title}
					description={[
						article.author && fullName(article.author),
						I18n.t(`category.${article.category}`),
						article.description.getPlainText(),
					]
						.filter(Boolean)
						.join(" - ")}
					ogImage={article.banner?.url}
					canonicalLink={article.reference ?? `${Config.APP_BASE_URL}${location.pathname}`}
				/> */}
				<ArticlePresentation article={article} />
				{!currentProfile && <Space />}
				{!currentProfile && <AppAuthIncentive message={I18n.t("article.auth.incentive", { name: article.title })} />}
			</SubContainer>
		</Container>
	);
};

const Container = styled(FullPage)`
	${stack(0, "flex-start", "center")};
`;

const SubContainer = styled(RoundedBlock)`
	max-width: 1128px; /* To be explained. */
	@media (max-width: 900px) {
		max-width: 100%;
	}
`;

const HeaderAction = styled.div`
	display: flex;
	align-items: center;
	${({ theme }) => theme.textStyles.primary};
	cursor: pointer;
	svg {
		margin-left: 5px;
		path {
			transition: fill 0.2s linear;
		}
	}
`;

const Space = styled.div`
	height: 167px;
`;
